import React, { useState } from 'react';
import { X, Play, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import Editor from '@monaco-editor/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function CompilerSidebar({ onClose }) {
    const [code, setCode] = useState('#include <stdio.h>\n\nint main() {\n    printf("Hello, World!\\n");\n    return 0;\n}');
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [running, setRunning] = useState(false);

    const runCode = async () => {
        setRunning(true);
        setOutput('');
        setError('');

        try {
            const response = await fetch(`${API}/compiler/run`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ code, input })
            });

            const result = await response.json();

            if (result.success) {
                setOutput(result.output || 'Program executed successfully (no output)');
                toast.success('Code executed successfully!');
            } else {
                setError(result.error);
                if (result.compilation_error) {
                    toast.error('Compilation failed');
                } else {
                    toast.error('Runtime error');
                }
            }
        } catch (err) {
            setError('Failed to execute code: ' + err.message);
            toast.error('Execution failed');
        } finally {
            setRunning(false);
        }
    };

    const downloadCode = () => {
        const element = document.createElement("a");
        const file = new Blob([code], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = "program.c";
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        toast.success('Code downloaded successfully!');
    };

    return (
        <div className="fixed right-0 top-0 h-screen w-[600px] bg-background-paper border-l border-white/5 z-50 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/5">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-code text-sm font-bold">C</span>
                    </div>
                    <div>
                        <h2 className="font-heading text-lg font-bold">C Compiler IDE</h2>
                        <p className="text-xs text-text-muted">Secure sandboxed execution</p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Code Editor */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <div className="p-4 border-b border-white/5">
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium">Code Editor</label>
                        <div className="flex gap-2">
                            <button
                                onClick={downloadCode}
                                className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors font-medium border border-white/10"
                            >
                                <Upload className="w-4 h-4 rotate-180" />
                                Download
                            </button>
                            <button
                                onClick={runCode}
                                disabled={running}
                                className="flex items-center gap-2 px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {running ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent"></div>
                                        Running...
                                    </>
                                ) : (
                                    <>
                                        <Play className="w-4 h-4" />
                                        Run Code
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden" data-testid="code-editor">
                    <Editor
                        height="100%"
                        defaultLanguage="c"
                        value={code}
                        onChange={(value) => setCode(value || '')}
                        theme="vs-dark"
                        options={{
                            minimap: { enabled: false },
                            fontSize: 14,
                            fontFamily: 'JetBrains Mono, monospace',
                            scrollBeyondLastLine: false,
                            automaticLayout: true,
                            tabSize: 4
                        }}
                    />
                </div>

                {/* Input Section */}
                <div className="border-t border-white/5 p-4">
                    <label className="block text-sm font-medium mb-2">Input (stdin)</label>
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Enter program input here..."
                        className="glass-input w-full h-20 resize-none font-code text-sm"
                    />
                </div>

                {/* Output Section */}
                <div className="border-t border-white/5 p-4 bg-background-subtle max-h-60 overflow-y-auto">
                    <label className="block text-sm font-medium mb-2">Output</label>
                    {output && (
                        <div className="bg-black/40 rounded-lg p-4 border border-primary/20">
                            <div className="flex items-start gap-2 mb-2">
                                <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                <span className="text-xs text-primary font-medium">Execution successful</span>
                            </div>
                            <pre className="font-code text-sm text-white whitespace-pre-wrap">{output}</pre>
                        </div>
                    )}
                    {error && (
                        <div className="bg-black/40 rounded-lg p-4 border border-accent-pink/20">
                            <div className="flex items-start gap-2 mb-2">
                                <AlertCircle className="w-4 h-4 text-accent-pink mt-0.5 flex-shrink-0" />
                                <span className="text-xs text-accent-pink font-medium">Error</span>
                            </div>
                            <pre className="font-code text-sm text-accent-pink whitespace-pre-wrap">{error}</pre>
                        </div>
                    )}
                    {!output && !error && (
                        <p className="text-sm text-text-muted italic">Output will appear here after running code</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CompilerSidebar;
